import { useBlockProps, RichText } from '@wordpress/block-editor';
import { useState } from '@wordpress/element';
import IconsModal from '../../../components/IconsModal';
import AllInspectorControls from './InspectorControls';
import { getIconClass } from './utils';
import useDynamicCount from './use-dynamic-count';

import '../style.scss';

export default function Edit( { attributes, setAttributes } ) {
	const { type, source } = attributes;
	const [ isIconsModalOpen, setIsIconsModalOpen ] = useState( false );
	const dynamicCount = useDynamicCount( attributes );

	// Use dynamic count as the display value when source is not manual.
	const displayAttributes =
		source && source !== 'manual' && dynamicCount !== null
			? { ...attributes, endValue: dynamicCount }
			: attributes;

	const blockProps = useBlockProps( {
		className: `brandy-counter brandy-counter--${ type }`,
		style: getEditorStyles( attributes ),
	} );

	return (
		<>
			<AllInspectorControls
				attributes={ attributes }
				setAttributes={ setAttributes }
				setIsIconsModalOpen={ setIsIconsModalOpen }
			/>
			<div { ...blockProps }>
				{ type === 'number' ? (
					<NumberPreview
						attributes={ displayAttributes }
						setAttributes={ setAttributes }
					/>
				) : (
					<CirclePreview
						attributes={ displayAttributes }
						setAttributes={ setAttributes }
					/>
				) }
			</div>
			<IconsModal
				isIconsModalOpen={ isIconsModalOpen }
				setIsIconsModalOpen={ setIsIconsModalOpen }
				icon={ {
					name: attributes.iconName,
					type: attributes.iconType ?? 'regular',
				} }
				setAttributes={ ( newAttrs ) =>
					setAttributes( {
						iconName: newAttrs.name,
						iconType: newAttrs.type,
					} )
				}
			/>
		</>
	);
}

function NumberPreview( { attributes, setAttributes } ) {
	const {
		layout,
		iconName,
		iconType,
		endValue,
		prefix,
		suffix,
		decimals,
		useThousandSeparator,
	} = attributes;

	const formatted = formatNumber( endValue, decimals, useThousandSeparator );
	const iconClass = getIconClass( iconName, iconType );

	return (
		<div
			className={ `brandy-counter__inner brandy-counter__layout--${ layout }` }
		>
			{ iconName && (
				<i className={ `brandy-counter__icon ${ iconClass }` } />
			) }
			<div className="brandy-counter__content">
				<div className="brandy-counter__value-wrapper">
					{ prefix && (
						<span className="brandy-counter__prefix">
							{ prefix }
						</span>
					) }
					<span className="brandy-counter__number">
						{ formatted }
					</span>
					{ suffix && (
						<span className="brandy-counter__suffix">
							{ suffix }
						</span>
					) }
				</div>
				<RichText
					tagName="div"
					className="brandy-counter__title"
					value={ attributes.title }
					onChange={ ( v ) => setAttributes( { title: v } ) }
					placeholder="Title"
				/>
				<RichText
					tagName="div"
					className="brandy-counter__description"
					value={ attributes.description }
					onChange={ ( v ) => setAttributes( { description: v } ) }
					placeholder="Description"
				/>
			</div>
		</div>
	);
}

function CirclePreview( { attributes, setAttributes } ) {
	const {
		iconName,
		iconType,
		endValue,
		maxValue,
		prefix,
		suffix,
		decimals,
		useThousandSeparator,
		strokeWidth,
	} = attributes;

	const iconClass = getIconClass( iconName, iconType );

	const formatted = formatNumber( endValue, decimals, useThousandSeparator );
	const radius = ( 100 - strokeWidth ) / 2;
	const circumference = 2 * Math.PI * radius;
	const progress = Math.min( endValue / ( maxValue || 1 ), 1 );
	const dashoffset = circumference * ( 1 - progress );

	return (
		<div className="brandy-counter__inner brandy-counter__type--circle">
			<div className="brandy-counter__circle-wrapper">
				<svg
					viewBox="0 0 100 100"
					className="brandy-counter__svg"
				>
					<circle
						className="brandy-counter__track"
						cx="50"
						cy="50"
						r={ radius }
					/>
					<circle
						className="brandy-counter__progress"
						cx="50"
						cy="50"
						r={ radius }
						strokeDasharray={ circumference }
						strokeDashoffset={ dashoffset }
					/>
				</svg>
				<div className="brandy-counter__circle-value">
					{ iconName && (
						<i className={ `brandy-counter__icon ${ iconClass }` } />
					) }
					<div className="brandy-counter__value-wrapper">
						{ prefix && (
							<span className="brandy-counter__prefix">
								{ prefix }
							</span>
						) }
						<span className="brandy-counter__number">
							{ formatted }
						</span>
						{ suffix && (
							<span className="brandy-counter__suffix">
								{ suffix }
							</span>
						) }
					</div>
					<RichText
						tagName="div"
						className="brandy-counter__title"
						value={ attributes.title }
						onChange={ ( v ) => setAttributes( { title: v } ) }
						placeholder="Title"
					/>
					<RichText
						tagName="div"
						className="brandy-counter__description"
						value={ attributes.description }
						onChange={ ( v ) => setAttributes( { description: v } ) }
						placeholder="Description"
					/>
				</div>
			</div>
		</div>
	);
}

function formatNumber( value, decimals, useSeparator ) {
	const fixed = Number( value ).toFixed( decimals );
	if ( ! useSeparator ) {
		return fixed;
	}
	const [ int, dec ] = fixed.split( '.' );
	const formatted = int.replace( /\B(?=(\d{3})+(?!\d))/g, ',' );
	return dec !== undefined ? `${ formatted }.${ dec }` : formatted;
}

function getEditorStyles( attributes ) {
	const vars = {};
	if ( attributes.numberFontSize != null ) {
		vars[ '--counter-number-font-size' ] = attributes.numberFontSize + 'px';
	}
	if ( attributes.numberColor ) {
		vars[ '--counter-number-color' ] = attributes.numberColor;
	}
	if ( attributes.numberFontWeight != null ) {
		vars[ '--counter-number-font-weight' ] = attributes.numberFontWeight;
	}
	if ( attributes.titleFontSize != null ) {
		vars[ '--counter-title-font-size' ] = attributes.titleFontSize + 'px';
	}
	if ( attributes.titleColor ) {
		vars[ '--counter-title-color' ] = attributes.titleColor;
	}
	if ( attributes.iconSize != null ) {
		vars[ '--counter-icon-size' ] = attributes.iconSize + 'px';
	}
	if ( attributes.iconColor ) {
		vars[ '--counter-icon-color' ] = attributes.iconColor;
	}
	if ( attributes.circleSize != null ) {
		vars[ '--counter-circle-size' ] = attributes.circleSize + 'px';
	}
	if ( attributes.strokeWidth != null ) {
		// Unitless — SVG stroke-width uses user units.
		vars[ '--counter-stroke-width' ] = attributes.strokeWidth;
	}
	if ( attributes.trackColor ) {
		vars[ '--counter-track-color' ] = attributes.trackColor;
	}
	if ( attributes.progressColor ) {
		vars[ '--counter-progress-color' ] = attributes.progressColor;
	}
	return vars;
}
